import { UseQueryOptions, useQuery } from 'react-query'

import { useReactiveState } from '@hooks/useReactiveState'
import { ApiError } from '@lib/api'
import utils from '@lib/utils'

export interface FallbackBuilderArguments<TData, TParams> {
  data?: TData | null
  previousParams: TParams
  initialParams: TParams
  retryCount: number
}

interface FallbackLoaderOptions<TData, TParams, TError = ApiError>
  extends Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'onSuccess' | 'onError' | 'queryFn'> {
  queryKeyPrefix: string
  params: TParams
  queryFn: (params: TParams) => Promise<TData>
  getFallbackParams: (args: FallbackBuilderArguments<TData, TParams>) => TParams | null
  onSuccess?: (data: TData, params: TParams | null, retryCount: number) => void
  onError?: (error: TError, params: TParams | null, retryCount: number) => void
}

/**
 * This hook is used when you need to automatically retry failed query with different parameters.
 *
 * When `getFallbackParams` returns null, the retry sequence will stop.
 */
const useFallbackLoader = <TData, TParams, TError = ApiError>({
  queryKeyPrefix,
  params,
  queryFn,
  getFallbackParams,
  onSuccess,
  onError,
  ...query
}: FallbackLoaderOptions<TData, TParams, TError>) => {
  const paramsHash = utils.common.hash(params)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [retryCount, setRetryCount] = useReactiveState(() => 0, [paramsHash])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const [currentParams, setCurrentParams] = useReactiveState(() => params, [paramsHash])

  const withFallback = (data: TData | null, finalizeRetries: () => void) => {
    const fallbackParams = getFallbackParams({
      data,
      previousParams: currentParams,
      initialParams: params,
      retryCount: retryCount + 1,
    })
    if (fallbackParams) {
      setRetryCount(retryCount + 1)
      setCurrentParams(fallbackParams)
    } else {
      finalizeRetries()
    }
  }

  const queryResult = useQuery<TData, TError, TData>({
    ...query,
    queryFn: () => queryFn(currentParams),
    queryKey: [queryKeyPrefix, currentParams],
    onSuccess: data => {
      withFallback(data, () => onSuccess?.(data, currentParams, retryCount))
    },
    onError: error => {
      withFallback(null, () => onError?.(error, currentParams, retryCount))
    },
  })

  return queryResult
}

export default useFallbackLoader
